import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EPackageTiers } from '@feathr/blackbox';
import { Nav, NavItem, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import { useLicense, useMinPackageTooltip } from '@feathr/extender/hooks/';
import { useLocalUrl, useRole } from '@feathr/extender/state';

interface ITooltipMessages {
  conversions?: React.ReactNode;
  implementations?: React.ReactNode;
}

type TTooltipByFeatureReturn = ITooltipMessages | undefined;

interface IDataNavProps {
  indented?: boolean;
}

function DataNav({ indented = true }: Readonly<IDataNavProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const { hasImports, hasSegments, hasSegmentsSome, isSudoer } = useRole();
  const { t } = useTranslation();

  const { implementationsPageVisible } = useLicense();

  const conversionsTooltip = useMinPackageTooltip('conversionPixels', 'data');
  const implementationsTooltip = useMinPackageTooltip('conversionPixels', 'data');

  const tooltipsByFeature = useMemo((): TTooltipByFeatureReturn => {
    // If user is sudoer or has access, return no tooltips
    if (isSudoer || implementationsPageVisible) {
      return undefined;
    }
    return {
      conversions: conversionsTooltip,
      implementations: implementationsTooltip,
    };
  }, [isSudoer, implementationsPageVisible, conversionsTooltip, implementationsTooltip]);

  const exploreNav = (
    <Nav indented={true} label={t('Important groups of people navigation')} type={'vertical'}>
      <NavItem
        data-command={'true'}
        data-command-description={t('View all people in community')}
        data-command-keywords={'community,people,all'}
        data-command-label={t('All People')}
        isForwardedFrom={localUrl('/data/explore')}
        to={localUrl('/data/explore/all/people')}
      >
        {t('All')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('View known people in community')}
        data-command-keywords={'community,people,known'}
        data-command-label={t('Known People')}
        to={localUrl('/data/explore/known/people')}
      >
        {t('Known')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('View anonymous people in community')}
        data-command-keywords={'community,people,anonymous'}
        data-command-label={t('Anonymous People')}
        to={localUrl('/data/explore/anonymous/people')}
      >
        {t('Anonymous')}
      </NavItem>
    </Nav>
  );

  const pixelNav = (
    <Nav indented={true} label={t('Implementation navigation')} type={'vertical'}>
      <NavItem
        data-command={'true'}
        data-command-description={t('Manage conversion tracking pixels')}
        data-command-keywords={'community,pixel,conversions,tracking'}
        data-command-label={t('Conversion Pixels')}
        to={localUrl('/data/pixel/conversions')}
        // Only show the tooltip if the user is not a sudoer and their package does not have the feature
        tooltip={tooltipsByFeature?.conversions}
        truncate={true}
      >
        {t('Conversion Pixels')}
      </NavItem>
      <TrackBlocked
        featureName={'implementations'}
        locked={!(implementationsPageVisible || isSudoer)}
        requiredPackage={implementationsPageVisible ? undefined : EPackageTiers.Essential}
      >
        <NavItem
          data-command={'true'}
          data-command-description={t('Community - Super Pixel implementations')}
          data-command-keywords={'community,pixel,implementations'}
          data-command-label={t('Implementations')}
          to={localUrl('/data/pixel/implementations')}
          tooltip={tooltipsByFeature?.implementations}
        >
          {t('Implementations')}
        </NavItem>
      </TrackBlocked>
    </Nav>
  );

  return (
    <Nav indented={indented} label={t('Community navigation')} type={'vertical'}>
      {hasSegments && (
        <NavMenu
          data-command={'true'}
          data-command-description={t('Community - Explore people')}
          data-command-keywords={'community,people,explore,all,known,anonymous'}
          data-command-label={t('People')}
          hasDefaultChild={true}
          menu={exploreNav}
          to={localUrl('/data/explore')}
        >
          {t('People')}
        </NavMenu>
      )}
      {(hasSegments || hasSegmentsSome) && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Community - Manage groups')}
          data-command-keywords={'community,groups,segments'}
          data-command-label={t('Groups')}
          to={localUrl('/data/segments')}
        >
          {t('Groups')}
        </NavItem>
      )}
      {hasSegments && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Community - Custom fields')}
          data-command-keywords={'community,custom,fields'}
          data-command-label={t('Custom Fields')}
          to={localUrl('/data/custom-fields')}
        >
          {t('Custom Fields')}
        </NavItem>
      )}
      {hasImports && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Community - Data imports')}
          data-command-keywords={'community,imports,data'}
          data-command-label={t('Imports')}
          to={localUrl('/data/imports')}
        >
          {t('Imports')}
        </NavItem>
      )}
      <NavMenu
        data-command={'true'}
        data-command-description={t('Community - Super Pixel conversions')}
        data-command-keywords={'community,pixel,conversions,tracking'}
        data-command-label={t('Super Pixel')}
        menu={pixelNav}
        to={localUrl('/data/pixel')}
      >
        {t('Super Pixel')}
      </NavMenu>
    </Nav>
  );
}

export default DataNav;
